import { useRoom } from "src/pages/home/room-detail/hooks"
import { PrimaryButton, SecondaryButton } from "src/pages/home/room-detail/sections/elements/Elements"
import DescriptionDetail from "src/shared/components/data-display/description-detail/DescriptionDetail"
import IncidenciasItem from "src/pages/home/room-detail/sections/items/Incidencias"
import DrawerDetail from "src/shared/components/data-display/DrawerDetail/DrawerDetail"
import DrawerDetailIcon from "src/shared/components/data-display/DrawerDetail/kit/DrawerDetailIcon/DrawerDetailIcon"
import DrawerDetailContent from "src/shared/components/data-display/DrawerDetail/kit/DrawerDetailContent/DrawerDetailContent"
import DrawerDetailText from "src/shared/components/data-display/DrawerDetail/kit/DrawerDetailText/DrawerDetailText"
import {
    Estados_Habitaciones,
    TiposAlojamientos,
    useActualizar_Colaboradores_TareasMutation,
} from "src/gql/schema"
import { useRoomDarwer } from "src/pages/home/room-detail/hooks/darwer"
import { useProfile } from "src/shared/hooks/useProfile"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import useSnackbar from "src/shared/hooks/useSnackbar"
import useLoadingState from "src/shared/hooks/useLoadingState"
import useIsColaboradorActive from "src/shared/hooks/useIsColaboradorActive"
import { useCleaningTypes } from "src/pages/home/room-detail/hooks/limpieza"
import { useVentaEnLineaAcciones } from "../../../hooks/useVentaEnLineaAcciones"

import "./Huesped.css"

const plural = (value: number, singular: string, pluralText: string) =>
    `${value} ${value === 1 ? singular : pluralText}`

const Huesped = ({
    isNoShow,
    onClean,
    onSwapColaborador,
    onSwapTipoLimpieza,
    onStartFinalizarLimpieza,
    onConfirmFinalizarLimpieza,
}: {
    // Se excedió el tiempo para hacer check in: solo se puede liberar la habitación
    isNoShow: boolean
    onClean: () => void
    onSwapColaborador: () => void
    onSwapTipoLimpieza: () => void
    // El detalle muestra el skeleton desde que se finaliza hasta que llega la habitación sin la limpieza
    onStartFinalizarLimpieza: () => void
    onConfirmFinalizarLimpieza: (success: boolean) => void
}) => {
    const room = useRoom()
    const renta = room?.ultima_renta
    const { usuario_id, hotel_id } = useProfile()
    const { formatCustomDate } = useFormatDate()
    const { showSnackbar } = useSnackbar()
    const { isLoading, toggleIsLoading, tryStartLoading } = useLoadingState()
    const { InactiveModal, validateIsColabActive } = useIsColaboradorActive()
    const [finalizarTarea] = useActualizar_Colaboradores_TareasMutation()
    const { onClose } = useRoomDarwer()
    const { canOperate, checkIn, solicitarLiberar, LiberarModal, isLoading: isLiberando } = useVentaEnLineaAcciones({
        onLiberada: onClose,
    })
    const cleanTypes = useCleaningTypes()
    const tareasLimpieza = room?.colaborador_tareas_sin_finalizar || []
    const enLimpieza = tareasLimpieza.length > 0
    const tipoLimpiezaLabel = cleanTypes.find(({ value }) => value === tareasLimpieza[0]?.tipo_limpieza)?.label || "-"

    const duracionEstancia = () => {
        if (!renta?.tarifa) {
            return ""
        }
        const hospedajes = 1 + (renta.hospedajes_extra || 0)
        if (renta.tarifa.tipo_alojamiento === TiposAlojamientos.Hotel) {
            return plural(hospedajes, "noche", "noches")
        }
        const horas = (renta.tarifa.duracion_renta || 0) * hospedajes + (renta.horas_extra || 0)
        return plural(horas, "hora", "horas")
    }

    const personasExtra = renta?.personas_extra || 0
    const personasExtraText = personasExtra > 0 ? `+${plural(personasExtra, "persona extra", "personas extra")}` : ""
    const responsableLiberacion = room?.ultimos_datos?.ultima_supervision?.colaborador
    const responsableLiberacionNombre = responsableLiberacion
        ? `${responsableLiberacion.nombre} ${responsableLiberacion.apellido_paterno} ${responsableLiberacion.apellido_materno}`
        : "-"

    // Finaliza la limpieza y la habitación regresa a venta en línea
    const onFinalizarLimpieza = () => {
        if (!tryStartLoading()) {
            return
        }
        onStartFinalizarLimpieza()
        finalizarTarea({
            variables: {
                datos_tarea: {
                    colaboradores_tareas_ids: tareasLimpieza.map((c) => c?.colaborador_tarea_id),
                    usuario_id,
                    hotel_id,
                    estado: Estados_Habitaciones.VentaEnLinea,
                },
            },
        })
            .then(() => {
                showSnackbar({
                    title: "Limpieza finalizada",
                    status: "success",
                    text: `La limpieza ha finalizado con éxito en la habitación **${room?.tipo_habitacion?.nombre} ${room?.numero_habitacion}**`,
                })
                onConfirmFinalizarLimpieza(true)
            })
            .catch(() => {
                showSnackbar({
                    title: "Error al finalizar limpieza",
                    status: "error",
                    text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente",
                })
                onConfirmFinalizarLimpieza(false)
            })
            .finally(() => toggleIsLoading({ value: false }))
    }

    return (
        <div className="animante__opacity-transform__ease room-detail--venta-en-linea__tab--huesped">
            <div className="room-detail--venta-en-linea__tab--huesped__list">
                {enLimpieza && (
                    <DescriptionDetail
                        icon="businessStar"
                        label="Tipo de limpieza"
                        style={{ width: "100%", padding: "12px" }}
                        onLink={canOperate ? validateIsColabActive(() => onSwapTipoLimpieza()) : undefined}
                        link={canOperate ? "Cambiar" : ""}
                        value={tipoLimpiezaLabel}
                    />
                )}
                <DrawerDetail>
                    <DrawerDetailIcon name="userFilled" />
                    <DrawerDetailContent>
                        <DrawerDetailText type="bold">Nombre completo del huésped</DrawerDetailText>
                        <DrawerDetailText type="normal">
                            {renta?.nombre_huesped || renta?.reserva?.nombre_huesped || "-"}
                        </DrawerDetailText>
                    </DrawerDetailContent>
                </DrawerDetail>
                <DrawerDetail>
                    <DrawerDetailIcon name="LockFill" />
                    <DrawerDetailContent>
                        <DrawerDetailText type="bold">Código de reserva</DrawerDetailText>
                        <DrawerDetailText type="normal">{renta?.folio ? `ER-${renta.folio.toString().padStart(3, "0")}` : "-"}</DrawerDetailText>
                    </DrawerDetailContent>
                </DrawerDetail>
                <DrawerDetail>
                    <DrawerDetailIcon name="calendarFill" />
                    <DrawerDetailContent>
                        <DrawerDetailText type="bold">Fecha de estancia</DrawerDetailText>
                        <DrawerDetailText type="normal">
                            {formatCustomDate(renta?.fecha_registro, "MMM, DD YYYY")}
                        </DrawerDetailText>
                        <DrawerDetailText type="bold">{duracionEstancia()}</DrawerDetailText>
                    </DrawerDetailContent>
                </DrawerDetail>
                <DrawerDetail>
                    <DrawerDetailIcon name="mailOpenFill" />
                    <DrawerDetailContent>
                        <DrawerDetailText type="bold">Correo</DrawerDetailText>
                        <DrawerDetailText type="normal">
                            {renta?.cliente?.correo || "-"}
                        </DrawerDetailText>
                    </DrawerDetailContent>
                </DrawerDetail>
                <DrawerDetail>
                    <DrawerDetailIcon name="BusinessGlobalFill" />
                    <DrawerDetailContent>
                        <DrawerDetailText type="bold">Origen de venta</DrawerDetailText>
                        {/* TODO: 1 este dato viene de back */}
                        <DrawerDetailText type="normal">App de reservas</DrawerDetailText>
                    </DrawerDetailContent>
                </DrawerDetail>
                <DrawerDetail>
                    <DrawerDetailIcon name="UserParentFill" />
                    <DrawerDetailContent>
                        <DrawerDetailText type="bold">Ocupación</DrawerDetailText>
                        <DrawerDetailText type="normal">
                            {plural(renta?.numero_personas || 0, "persona", "personas")}
                        </DrawerDetailText>
                        {personasExtraText && <DrawerDetailText type="bold">{personasExtraText}</DrawerDetailText>}
                    </DrawerDetailContent>
                </DrawerDetail>
                <DrawerDetail>
                    <DrawerDetailIcon name="CheckBoxMultipleFill" />
                    <DrawerDetailContent>
                        <DrawerDetailText type="bold">Último responsable de liberación</DrawerDetailText>
                        <DrawerDetailText type="normal">{responsableLiberacionNombre}</DrawerDetailText>
                    </DrawerDetailContent>
                </DrawerDetail>
                <IncidenciasItem />
            </div>
            {canOperate && enLimpieza && (
                <>
                    <PrimaryButton
                        text={"Finalizar limpieza"}
                        disabled={isLoading}
                        onClick={validateIsColabActive(onFinalizarLimpieza)}
                        style={{ marginBottom: "12px" }}
                    />
                    <SecondaryButton
                        text={"Cambiar o agregar camarista"}
                        onClick={validateIsColabActive(() => onSwapColaborador())}
                    />
                </>
            )}
            {canOperate && !enLimpieza && isNoShow && (
                <PrimaryButton
                    text={"Liberar habitación"}
                    disabled={isLiberando}
                    onClick={validateIsColabActive(() => solicitarLiberar(room))}
                />
            )}
            {canOperate && !enLimpieza && !isNoShow && (
                <>
                    <PrimaryButton
                        text={"Check in"}
                        onClick={validateIsColabActive(() => checkIn(room))}
                        style={{ marginBottom: "12px" }}
                    />
                    <SecondaryButton text={"Limpiar habitación"} onClick={validateIsColabActive(() => onClean())} />
                </>
            )}
            {LiberarModal}
            {InactiveModal}
        </div>
    )
}

export default Huesped
